import {IRouteProps} from "../../../../ts/manager/view/ViewInterface";
import {getManager} from "../../../../ts/manager/Manager";
import {getModelFactory} from "../../../../ts/model-factory/ModelFactory";
import StateViewController from "../../../vc/StateViewController";
import {assert} from "../../../../ts/common/Assert";
import {dateFormat} from "../../../../ts/common/DateFormat";
import React, {RefObject, useEffect, useRef, useState} from "react";
import {SearchRelatedRouteVO} from "../../../../ts/route/search/SearchBasicPageListRouteFactory";
import AppButton from "../../../element/button/AppButton";
import BasicForm from "../../../component/form/BasicForm";
import {IFormProps} from "../../../component/form/model/FormModel";
import ApiResponseVO from "../../../../ts/manager/api/ApiResonseVO";
import {dispatchRedirectTo} from "../../../../ts/event/DispatchEvent";
import {MessageDataChoice} from "../../../../ts/data/subject/message/MessageDataFactory";
import {IStringObject} from "../../../../ts/common/SimpleInterface";

interface MessageEntryDeleteFormProps extends IFormProps
{
    messageID: number;
}

interface MessageEntryActionFormProps extends IFormProps
{
    message: MessageDataChoice;
}

class MessageEntryDeleteForm extends BasicForm
{
    props: MessageEntryDeleteFormProps;
    hideSuccessMessage:boolean = true;

    constructor(props: MessageEntryDeleteFormProps)
    {
        super(props);

        this.props = props;
    }

    async handleSubmitItems(): Promise<ApiResponseVO>
    {
        return await getManager().api.fetchData("/message/" + this.props.messageID + "/delete", "DELETE", null, true);
    }

    async getAdditionalData(): Promise<void>
    {
        getModelFactory().messagePageListData.resetDataFromEntryID(this.props.messageID.toString());
    }

    renderComponent()
    {
        return <>
            {this.formAlerts()}
        </>
    }
}


class MessageEntryActionForm extends BasicForm
{
    props: MessageEntryActionFormProps;
    hideSuccessMessage:boolean = true;
    actionClasses:IStringObject = {
        reject: "btn-secondary"
    }

    constructor(props: MessageEntryActionFormProps)
    {
        super(props);

        this.props = props;
    }

    async handleSubmitItems(_: string[]|null, submitType: string): Promise<ApiResponseVO>
    {
        return await getManager().api.fetchData("/message/" + this.props.message.id + "/action/" + submitType, "POST", null, true);
    }

    async getAdditionalData(): Promise<void>
    {
        const organisationID = this.props.message.sourceInfo.organisationID;
        if(organisationID){
            const userDataModel = getModelFactory().organisationUserData.organisationUserDataModel(organisationID);
            userDataModel.resetPopulated();
            getModelFactory().postData.onPostStateChange(organisationID, "organisation");
        }
        const messageEntryModel = getModelFactory().messageData.getEntryDataModel(this.props.message.messageID);
        messageEntryModel.resetPopulated();
        return messageEntryModel.requirePopulated().then(apiError =>
        {
            if (apiError)
            {
                return Promise.reject(apiError);
            }
        })
    }

    renderComponent()
    {
        return <>

            <form
                onSubmit={this.handleFormSubmit}
                className={"custom-form "+(this.isDisabled() ? "content-disabled" : "")}
            >
                <div className="row">

                    <div className="col-12">
                        {this.formAlerts()}
                        {this.requiredFieldsMessage()}
                    </div>
                    <div className="col-12 text-end">
                        {this.props.message.actions.map((actionVO, index) =>
                        {
                            const btnClass = this.actionClasses[actionVO.actionID] ?? "btn-primary";
                            return <AppButton
                                type="submit"
                                key={index}
                                loading={this.loading}
                                value={actionVO.actionID}
                                className={"btn btn-block " + btnClass}
                                disabled={this.isSubmitDisabled()}
                            >
                                {actionVO.text}
                            </AppButton>
                        })}
                    </div>
                </div>
            </form>
        </>
    }
}

const MessageEntryPage = (props:IRouteProps) =>
{
    const messageID = parseInt(getManager().navigation.model.routeIDs.message_list_route_id ?? "0");
    const messageEntryModel = getModelFactory().messageData.getEntryDataModel(messageID);
    const message = messageEntryModel.getMessage();
    const [relatedRouteVO, setRelatedRouteVO] = useState<SearchRelatedRouteVO|null>(null);
    const deleteFormRef = useRef<MessageEntryDeleteForm|null>(null);
    const actionFormRef = useRef<MessageEntryActionForm|null>(null);

    useEffect(() =>
    {
        const pageListRouteModel = getModelFactory().messagePageListRoute.getDefaultModel();
        pageListRouteModel.getRelatedRoutes(messageID.toString()).then((vo) => {
            setRelatedRouteVO(vo)
        });
    }, []);

    const viewState = StateViewController({
        routeProps: props,
        assert: [message],
        dataModels: [messageEntryModel]
    })

    if(viewState.overrideView){
        return viewState.overrideView;
    }

    const getDeleteForm = ():MessageEntryDeleteForm|null =>
    {
        return (deleteFormRef.current as MessageEntryDeleteForm) ?? null;
    }

    const getActionForm = ():MessageEntryActionForm|null =>
    {
        return (actionFormRef.current as MessageEntryActionForm) ?? null;
    }

    const deleteMessage = () => {
        getDeleteForm()?.processFormSubmit();
    }

    const onDeleteSubmit = () => {
        const redirectTo = (relatedRouteVO?.nextEntry ?? (relatedRouteVO?.previousEntryParentPage ?? null))?.href ?? "";
        if(redirectTo){

            dispatchRedirectTo(redirectTo)
        }
    }

    const anyDisabled = () => {
        return (getDeleteForm()?.isDisabled() ?? false) || (getActionForm()?.isDisabled() ?? false)
    }

    assert(message);

    return <section
        className={"content-block "+(anyDisabled() ? "content-disabled" : "")}
    >
        <div className="container">
            <div className="row">
                <div className="col-6">
                    {relatedRouteVO && (relatedRouteVO?.entryIndex ?? -1) >= 0 ? (relatedRouteVO.entryIndex + 1) + "/" + relatedRouteVO.numberOfEntries : ""}
                </div>
                <div className="col-6 text-end">
                    <AppButton
                        nonButton={true}
                        className="me-2"
                        disabled={!relatedRouteVO?.parentPage}
                        href={relatedRouteVO?.parentPage?.getHref() ?? "#"}
                    >
                        <i
                            className="fa fa-solid fa-arrow-left"
                            aria-hidden="true"
                        ></i>
                    </AppButton>
                    <AppButton
                        nonButton={true}
                        className="me-2"
                        disabled={!relatedRouteVO?.previousEntry}
                        href={relatedRouteVO?.previousEntry?.getHref() ?? "#"}
                    >
                        <i
                            className="fa fa-solid fa-arrow-up"
                            aria-hidden="true"
                        ></i>
                    </AppButton>
                    <AppButton
                        nonButton={true}
                        className="me-2"
                        disabled={!relatedRouteVO?.nextEntry}
                        href={relatedRouteVO?.nextEntry?.getHref() ?? "#"}
                    >
                        <i
                            className="fa fa-solid fa-arrow-down"
                            aria-hidden="true"
                        ></i>
                    </AppButton>
                    <AppButton
                        nonButton={true}
                        onClick={deleteMessage}
                        disabled={!relatedRouteVO || message.canNotDelete}
                    >
                        <i
                            className="fa fa-solid fa-trash"
                            aria-hidden="true"
                        ></i>
                    </AppButton>
                </div>
            </div>
            <div className="row">
                <div className="col-12">
                    <MessageEntryDeleteForm
                        ref={deleteFormRef}
                        routeProps={props}
                        messageID={messageID}
                        onSubmit={onDeleteSubmit}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-12">
                    <h2>{message.title}</h2>
                </div>
            </div>
            <div className="row">
                <div className="col-12">
                    <p className="smaller"><strong>From:</strong> {message.fromUser.getName()}</p>
                </div>
                <div className="col-12">
                    <p className="smaller">{message.created ? dateFormat(message.created , {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                    }) : ""}</p>
                </div>
            </div>
            <div className="row">
                <div className="col-12">
                    <p>{message.body}</p>
                </div>
            </div>
            {!!message.actions.length && <>
                <div className="row">
                    <div className="col-12">
                        <MessageEntryActionForm
                            ref={actionFormRef}
                            routeProps={props}
                            message={message}
                        />
                    </div>
                </div>
            </>}
        </div>
    </section>
}

export default MessageEntryPage;